import { initializeApp } from "firebase/app";
import { 
  getFirestore, 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  setDoc, 
  query, 
  where, 
  serverTimestamp,
  runTransaction,
  addDoc,
  deleteDoc,
  orderBy,
  startAfter,
  limit,
  arrayUnion,
  increment
} from "firebase/firestore";
import { getAuth, setPersistence, browserSessionPersistence } from "firebase/auth";
import { getStorage } from "firebase/storage";
import { getAnalytics, isSupported, logEvent } from "firebase/analytics";
import { getFunctions } from "firebase/functions";
import { firebaseConfig, validateConfig } from "./FirebaseConfig";
import debug from '../../utils/debug'; // Import debug utility

let firebaseApp;
let firebaseDb;
let firebaseAuth;
let firebaseStorage;
let firebaseAnalytics;
let firebaseFunctions;


// Initialize Firebase synchronously first, then handle async parts
try {
  debug.component.mount('FirebaseIndex');
  
  if (!validateConfig()) {
    const error = new Error("Invalid Firebase configuration. Check your environment variables.");
    debug.error('FirebaseIndex', 'Configuration validation failed', error);
    throw error;
  }

  // Initialize core Firebase services synchronously
  if (!firebaseApp) {
    firebaseApp = initializeApp(firebaseConfig);
    debug.firebase('App', 'Firebase app initialized');
  }
  if (!firebaseDb) {
    firebaseDb = getFirestore(firebaseApp);
    debug.firebase('Firestore', 'Firestore initialized');
  }
  if (!firebaseAuth) {
    firebaseAuth = getAuth(firebaseApp);
    debug.firebase('Auth', 'Auth initialized');
  }
  if (!firebaseStorage) {
    firebaseStorage = getStorage(firebaseApp);
    debug.firebase('Storage', 'Storage initialized');
  }
  if (!firebaseFunctions) {
    firebaseFunctions = getFunctions(firebaseApp, 'europe-west2');
    debug.firebase('Functions', 'Functions initialized');
  }
  
  // Handle async initialization for optional services
  (async () => {
    try {
      // Set Firebase auth persistence for better session management
      if (firebaseAuth) {
        try {
          await setPersistence(firebaseAuth, browserSessionPersistence);
          debug.firebase('Auth', 'Auth persistence set to session');
        } catch (persistenceError) {
          debug.warn('Auth', 'Failed to set persistence, continuing with default', persistenceError);
        }
      }
      
      // Initialize analytics if supported
      if (!firebaseAnalytics) {
        const analyticsSupported = await isSupported();
        if (analyticsSupported) {
          firebaseAnalytics = getAnalytics(firebaseApp);
          debug.firebase('Analytics', 'Analytics initialized');
        } else {
          debug.log('FirebaseIndex', 'Analytics not supported');
        }
      }
    } catch (error) {
      debug.warn('FirebaseIndex', 'Async Firebase initialization warning:', error);
    }
  })();
  
} catch (error) {
  debug.error('FirebaseIndex', 'Firebase initialization error:', error);
  throw error;
}

const getFirebaseApp = () => {
  if (!firebaseApp) {
    const error = new Error("Firebase App not initialized");
    debug.error('FirebaseIndex', 'Firebase app access error', error);
    throw error;
  }
  return firebaseApp;
};

const getFirestoreDb = () => {
  if (!firebaseDb) {
    const error = new Error("Firestore not initialized");
    debug.error('FirebaseIndex', 'Firestore access error', error);
    throw error;
  }
  return firebaseDb;
};

const getFirebaseAuth = () => {
  if (!firebaseAuth) {
    const error = new Error("Auth not initialized");
    debug.error('FirebaseIndex', 'Auth access error', error);
    throw error;
  }
  return firebaseAuth;
};

const getFirebaseStorage = () => {
  if (!firebaseStorage) {
    const error = new Error("Storage not initialized");
    debug.error('FirebaseIndex', 'Storage access error', error);
    throw error;
  }
  return firebaseStorage;
};

const getFirebaseAnalytics = () => {
  if (!firebaseAnalytics) {
    debug.log('FirebaseIndex', 'Analytics not initialized or unsupported');
    return null;
  }
  return firebaseAnalytics;
};

const getFirebaseFunctions = () => {
  if (!firebaseFunctions) {
    const error = new Error("Functions not initialized");
    debug.error('FirebaseIndex', 'Functions access error', error);
    throw error;
  }
  return firebaseFunctions;
};

// Direct exports that will be available after initialization
export let app, db, auth, storage, analytics, functions;

// Initialize and assign exports
try {
  app = getFirebaseApp();
  db = getFirestoreDb();
  auth = getFirebaseAuth();
  storage = getFirebaseStorage();
  analytics = getFirebaseAnalytics();
  functions = getFirebaseFunctions();
} catch (error) {
  debug.error('FirebaseIndex', 'Error during export initialization', error);
}

export {
  getFirebaseApp,
  getFirestoreDb,
  getFirebaseAuth,
  getFirebaseStorage,
  getFirebaseAnalytics,
  getFirebaseFunctions
};

export function validateCollectionName(collectionName) {
  if (!collectionName || typeof collectionName !== "string" || collectionName.trim() === "") {
    const error = new Error("Invalid collection name: must be a non-empty string");
    debug.error('FirebaseIndex', 'Collection name validation failed', { collectionName });
    throw error;
  }
}

export function validateDocId(docId) {
  if (!docId || typeof docId !== "string" || docId.trim() === "") {
    const error = new Error("Invalid document ID: must be a non-empty string");
    debug.error('FirebaseIndex', 'Document ID validation failed', { docId });
    throw error;
  }
}

export function validateData(data) {
  if (!data || typeof data !== "object" || Array.isArray(data) || Object.keys(data).length === 0) {
    const error = new Error("Invalid data: must be a non-empty object");
    debug.error('FirebaseIndex', 'Data validation failed', { dataType: typeof data });
    throw error;
  }
}

export const processBookings = (snapshot) => {
  const bookings = { confirmed: [], pending: [] };
  if (!snapshot || snapshot.empty) return bookings;
  
  debug.log('FirebaseIndex', `Processing ${snapshot.size} bookings`);
  
  snapshot.forEach((doc) => {
    const booking = { id: doc.id, ...doc.data() };
    if (booking.status === "confirmed") bookings.confirmed.push(booking);
    else if (booking.status === "pending") bookings.pending.push(booking);
  });
  
  debug.log('FirebaseIndex', 'Bookings processed', { 
    confirmed: bookings.confirmed.length, 
    pending: bookings.pending.length 
  });
  
  return bookings;
};

export const processEvents = (snapshot) => {
  const events = { active: [], completed: [] };
  if (!snapshot || snapshot.empty) return events;
  
  debug.log('FirebaseIndex', `Processing ${snapshot.size} events`);
  
  snapshot.forEach((doc) => {
    const event = { id: doc.id, ...doc.data() };
    if (event.status === "active") events.active.push(event);
    else if (event.status === "completed") events.completed.push(event);
  });
  
  debug.log('FirebaseIndex', 'Events processed', { 
    active: events.active.length, 
    completed: events.completed.length 
  });
  
  return events;
};

export const fetchCompleteUserProfile = async (userUID, userType) => {
  try {
    debug.firebase('Profile', `Fetching complete ${userType} profile`, { userUID });
    
    validateDocId(userUID);
    if (!["Talent", "Organiser"].includes(userType)) {
      const error = new Error("Invalid user type: must be 'Talent' or 'Organiser'");
      debug.error('FirebaseIndex', 'User type validation failed', { userType });
      throw error;
    }

    const userDoc = await getDoc(doc(db, "Users", userUID));
    if (!userDoc.exists()) {
      const error = new Error(`${userType} user not found`);
      debug.error('FirebaseIndex', 'User not found', { userUID, userType });
      throw error;
    }

    const userProfile = userDoc.data().profile;
    if (userProfile.userType !== userType) {
      const error = new Error(`User type mismatch: Expected ${userType}, found ${userProfile.userType}`);
      debug.error('FirebaseIndex', 'User type mismatch', { 
        expected: userType, 
        found: userProfile.userType 
      });
      throw error;
    }

    if (userType === "Talent") {
      debug.log('FirebaseIndex', 'Fetching talent collections', { userUID });
      
      const talentBlueprint = await getDoc(doc(db, "TalentBlueprint", userUID));
      if (!talentBlueprint.exists()) {
        const error = new Error("Talent blueprint not found");
        debug.error('FirebaseIndex', 'Talent blueprint not found', { userUID });
        throw error;
      }

      const [
        studioDoc, 
        performanceDoc, 
        pricingDoc, 
        complianceDoc, 
        analyticsDoc, 
        dashboardDoc,
        technicalRiderDoc,
        logisticsRiderDoc,
        talentCrewDoc,
        bookingsSnapshot
      ] = await Promise.all([
        getDoc(doc(db, "TalentStudio", userUID)),
        getDoc(doc(db, "PerformanceHub", userUID)),
        getDoc(doc(db, "PricingPortfolio", userUID)),
        getDoc(doc(db, "ComplianceVault", userUID)),
        getDoc(doc(db, "TalentAnalytics", userUID)),
        getDoc(doc(db, "Dashboard", userUID)),
        getDoc(doc(db, "TechnicalRiderWorkshop", userUID)),
        getDoc(doc(db, "LogisticsHospitalityRider", userUID)),
        getDoc(doc(db, "TalentCrew", userUID)),
        getDocs(query(collection(db, "Bookings"), where("talentUID", "==", userUID))),
      ]);
      
      debug.log('FirebaseIndex', 'Talent profile fetched successfully', { userUID });

      return {
        profile: userDoc.data(),
        blueprint: talentBlueprint.data(),
        studio: studioDoc.exists() ? studioDoc.data() : null,
        performance: performanceDoc.exists() ? performanceDoc.data() : null,
        pricing: pricingDoc.exists() ? pricingDoc.data() : null,
        compliance: complianceDoc.exists() ? complianceDoc.data() : null,
        analytics: analyticsDoc.exists() ? analyticsDoc.data() : null,
        dashboard: dashboardDoc.exists() ? dashboardDoc.data() : null,
        technicalRider: technicalRiderDoc.exists() ? technicalRiderDoc.data() : null,
        logisticsRider: logisticsRiderDoc.exists() ? logisticsRiderDoc.data() : null,
        crew: talentCrewDoc.exists() ? talentCrewDoc.data() : null,
        bookings: processBookings(bookingsSnapshot),
      };
    } else {
      debug.log('FirebaseIndex', 'Fetching organiser collections', { userUID });
      
      const organiserBlueprint = await getDoc(doc(db, "OrganiserBlueprint", userUID));
      if (!organiserBlueprint.exists()) {
        const error = new Error("Organiser blueprint not found");
        debug.error('FirebaseIndex', 'Organiser blueprint not found', { userUID });
        throw error;
      }

      const [analyticsDoc, dashboardDoc, complianceDoc, eventsSnapshot] = await Promise.all([
        getDoc(doc(db, "OrganiserAnalytics", userUID)),
        getDoc(doc(db, "Dashboard", userUID)),
        getDoc(doc(db, "ComplianceVault", userUID)),
        getDocs(query(collection(db, "Bookings"), where("organiserUID", "==", userUID))),
      ]);
      
      debug.log('FirebaseIndex', 'Organiser profile fetched successfully', { userUID });

      return {
        profile: userDoc.data(),
        blueprint: organiserBlueprint.data(),
        analytics: analyticsDoc.exists() ? analyticsDoc.data() : null,
        dashboard: dashboardDoc.exists() ? dashboardDoc.data() : null,
        compliance: complianceDoc.exists() ? complianceDoc.data() : null,
        bookings: processBookings(eventsSnapshot),
      };
    }
  } catch (error) {
    debug.error('FirebaseIndex', 'Error fetching User profile:', error);
    throw error;
  }
};

export const saveToFirestore = async (collectionData, userUID, userType) => {
  try {
    debug.firebase('Firestore', `Saving ${userType} data`, { userUID });
    
    validateDocId(userUID);
    if (!["Talent", "Organiser"].includes(userType)) {
      const error = new Error("Invalid user type: must be 'Talent' or 'Organiser'");
      debug.error('FirebaseIndex', 'User type validation failed', { userType });
      throw error;
    }
    validateData(collectionData);

    const userDocRef = doc(db, "Users", userUID);
    await updateDoc(userDocRef, {
      "profile.userType": userType,
      "profile.lastUpdated": serverTimestamp()
    }, { merge: true });

    if (userType === "Talent") {
      debug.log('FirebaseIndex', 'Saving talent blueprint', { userUID });
      
      await setDoc(doc(db, "TalentBlueprint", userUID), {
        ...collectionData.blueprint,
        userUID: userUID,
        visibilityStatus: false,
        status: "active"
      }, { merge: true });

      const talentCollections = [
        { name: "TalentStudio", data: collectionData.studio },
        { name: "PerformanceHub", data: collectionData.performance },
        { name: "PricingPortfolio", data: collectionData.pricing },
        { name: "ComplianceVault", data: collectionData.compliance },
        { name: "TechnicalRiderWorkshop", data: collectionData.technicalRider },
        { name: "LogisticsHospitalityRider", data: collectionData.logisticsRider },
        { name: "TalentCrew", data: collectionData.crew },
        { name: "Dashboard", data: collectionData.dashboard }
      ];

      for (const collection of talentCollections) {
        if (collection.data) {
          debug.log('FirebaseIndex', `Saving talent ${collection.name}`, { userUID });
          
          await setDoc(doc(db, collection.name, userUID), {
            ...collection.data,
            userUID: userUID
          }, { merge: true });
        }
      }

      debug.log('FirebaseIndex', 'Creating/updating talent analytics', { userUID });
      
      await setDoc(doc(db, "TalentAnalytics", userUID), {
        userUID: userUID,
        bookingMetrics: { totalBookedGigs: 0, totalCompletedGigs: 0 },
        profileViews: { totalViews: 0, uniqueVisitors: 0 },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } else {
      debug.log('FirebaseIndex', 'Saving organiser blueprint', { userUID });
      
      await setDoc(doc(db, "OrganiserBlueprint", userUID), {
        ...collectionData.blueprint,
        userUID: userUID,
        visibilityStatus: false,
        status: "active"
      }, { merge: true });

      const organiserCollections = [
        { name: "ComplianceVault", data: collectionData.compliance },
        { name: "Dashboard", data: collectionData.dashboard }
      ];

      for (const collection of organiserCollections) {
        if (collection.data) {
          debug.log('FirebaseIndex', `Saving organiser ${collection.name}`, { userUID });
          
          await setDoc(doc(db, collection.name, userUID), {
            ...collection.data,
            userUID: userUID
          }, { merge: true });
        }
      }

      debug.log('FirebaseIndex', 'Creating/updating organiser analytics', { userUID });
      
      await setDoc(doc(db, "OrganiserAnalytics", userUID), {
        userUID: userUID,
        bookingMetrics: { totalBookings: 0, bookingsThisMonth: 0 },
        talentEngagementMetrics: { totalTalentsEngaged: 0 },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
    }

    debug.firebase('Firestore', `${userType} data saved successfully`, { userUID });
    return { success: true, userUID };
  } catch (error) {
    debug.error('FirebaseIndex', 'Error saving to Firestore:', error);
    throw error;
  }
};

export const updateUserProfileCollection = async (collectionName, userUID, data) => {
  try {
    debug.firebase('Firestore', `Updating ${collectionName}`, { userUID });
    
    validateCollectionName(collectionName);
    validateDocId(userUID);
    validateData(data);
    
    const docRef = doc(db, collectionName, userUID);
    await updateDoc(docRef, { 
      ...data, 
      updatedAt: serverTimestamp() 
    });
    
    debug.log('FirebaseIndex', `Updated ${collectionName}`, { userUID });
    return true;
  } catch (error) {
    debug.error('FirebaseIndex', `Error updating ${collectionName}`, { userUID, error });
    throw error;
  }
};

export const runTransactionOperation = async (transactionFn) => {
  try {
    debug.firebase('Firestore', 'Starting transaction operation');
    const result = await runTransaction(db, transactionFn);
    debug.firebase('Firestore', 'Transaction completed successfully');
    return result;
  } catch (error) {
    debug.error('FirebaseIndex', 'Transaction failed:', error);
    throw error;
  }
};

export const getCollection = async (collectionName) => {
  try {
    debug.firebase('Firestore', `Fetching collection: ${collectionName}`);
    
    validateCollectionName(collectionName);
    const querySnapshot = await getDocs(collection(db, collectionName));
    
    debug.log('FirebaseIndex', `Fetched ${querySnapshot.size} documents from ${collectionName}`);
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    debug.error('FirebaseIndex', 'Error fetching collection:', error);
    throw error;
  }
};

export const getDocument = async (collectionName, docId) => {
  try {
    debug.firebase('Firestore', `Fetching document: ${collectionName}/${docId}`);
    
    validateCollectionName(collectionName);
    validateDocId(docId);
    const docRef = doc(db, collectionName, docId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      debug.log('FirebaseIndex', `Document fetched: ${collectionName}/${docId}`);
      return { id: docSnap.id, ...docSnap.data() };
    } else {
      debug.log('FirebaseIndex', `Document not found: ${collectionName}/${docId}`);
      return null;
    }
  } catch (error) {
    debug.error('FirebaseIndex', 'Error fetching document:', error);
    throw error;
  }
};

// Technical Rider Workshop Collection Operations
export const saveTechnicalRider = async (userUID, riderData) => {
  try {
    debug.firebase('Firestore', 'Saving technical rider', { userUID });
    
    validateDocId(userUID);
    validateData(riderData);
    
    await setDoc(doc(db, "TechnicalRiderWorkshop", userUID), {
      ...riderData,
      userUID,
      updatedAt: serverTimestamp()
    }, { merge: true });
    
    debug.log('FirebaseIndex', 'Technical rider saved successfully', { userUID });
    return true;
  } catch (error) {
    debug.error('FirebaseIndex', 'Error saving technical rider:', error);
    throw error;
  }
};

export const getTechnicalRider = async (userUID) => {
  try {
    debug.firebase('Firestore', 'Fetching technical rider', { userUID });
    
    validateDocId(userUID);
    const riderDoc = await getDoc(doc(db, "TechnicalRiderWorkshop", userUID));
    
    if (riderDoc.exists()) {
      debug.log('FirebaseIndex', 'Technical rider fetched successfully', { userUID });
      return riderDoc.data();
    } else {
      debug.log('FirebaseIndex', 'Technical rider not found', { userUID });
      return null;
    }
  } catch (error) {
    debug.error('FirebaseIndex', 'Error fetching technical rider:', error);
    throw error;
  }
};

// Logistics & Hospitality Rider Collection Operations
export const saveLogisticsRider = async (userUID, riderData) => {
  try {
    debug.firebase('Firestore', 'Saving logistics rider', { userUID });
    
    validateDocId(userUID);
    validateData(riderData);
    
    await setDoc(doc(db, "LogisticsHospitalityRider", userUID), {
      ...riderData,
      userUID,
      updatedAt: serverTimestamp()
    }, { merge: true });
    
    debug.log('FirebaseIndex', 'Logistics rider saved successfully', { userUID });
    return true;
  } catch (error) {
    debug.error('FirebaseIndex', 'Error saving logistics rider:', error);
    throw error;
  }
};

export const getLogisticsRider = async (userUID) => {
  try {
    debug.firebase('Firestore', 'Fetching logistics rider', { userUID });
    
    validateDocId(userUID);
    const riderDoc = await getDoc(doc(db, "LogisticsHospitalityRider", userUID));
    
    if (riderDoc.exists()) {
      debug.log('FirebaseIndex', 'Logistics rider fetched successfully', { userUID });
      return riderDoc.data();
    } else {
      debug.log('FirebaseIndex', 'Logistics rider not found', { userUID });
      return null;
    }
  } catch (error) {
    debug.error('FirebaseIndex', 'Error fetching logistics rider:', error);
    throw error;
  }
};

// Talent Crew Collection Operations
export const saveTalentCrew = async (userUID, crewData) => {
  try {
    debug.firebase('Firestore', 'Saving talent crew', { userUID });
    
    validateDocId(userUID);
    validateData(crewData);
    
    await setDoc(doc(db, "TalentCrew", userUID), {
      ...crewData,
      userUID,
      updatedAt: serverTimestamp()
    }, { merge: true });
    
    debug.log('FirebaseIndex', 'Talent crew saved successfully', { userUID });
    return true;
  } catch (error) {
    debug.error('FirebaseIndex', 'Error saving talent crew:', error);
    throw error;
  }
};

export const getTalentCrew = async (userUID) => {
  try {
    debug.firebase('Firestore', 'Fetching talent crew', { userUID });
    
    validateDocId(userUID);
    const crewDoc = await getDoc(doc(db, "TalentCrew", userUID));
    
    if (crewDoc.exists()) {
      debug.log('FirebaseIndex', 'Talent crew fetched successfully', { userUID });
      return crewDoc.data();
    } else {
      debug.log('FirebaseIndex', 'Talent crew not found', { userUID });
      return null;
    }
  } catch (error) {
    debug.error('FirebaseIndex', 'Error fetching talent crew:', error);
    throw error;
  }
};

// Dashboard Collection Operations
export const updateDashboardAccess = async (userUID, userType) => {
  try {
    debug.firebase('Firestore', 'Updating dashboard access', { userUID, userType });
    
    validateDocId(userUID);
    
    const dashboardRef = doc(db, "Dashboard", userUID);
    const dashboardSnap = await getDoc(dashboardRef);
    
    if (dashboardSnap.exists()) {
      await updateDoc(dashboardRef, {
        accessDate: serverTimestamp(),
        userType
      });
    } else {
      await setDoc(dashboardRef, {
        userUID,
        userType,
        accessDate: serverTimestamp(),
        profileCompletionStatus: false,
        lastPromptDate: null
      });
    }
    
    debug.log('FirebaseIndex', 'Dashboard access updated', { userUID });
    return true;
  } catch (error) {
    debug.error('FirebaseIndex', 'Error updating dashboard access:', error);
    throw error;
  }
};

export const updateProfileCompletionStatus = async (userUID, isComplete) => {
  try {
    debug.firebase('Firestore', 'Updating profile completion status', { userUID, isComplete });
    
    validateDocId(userUID);
    
    const dashboardRef = doc(db, "Dashboard", userUID);
    await updateDoc(dashboardRef, {
      profileCompletionStatus: isComplete,
      lastPromptDate: isComplete ? null : serverTimestamp()
    });
    
    debug.log('FirebaseIndex', 'Profile completion status updated', { userUID, isComplete });
    return true;
  } catch (error) {
    debug.error('FirebaseIndex', 'Error updating profile completion status:', error);
    throw error;
  }
};

// Messages Collection Operations
export const sendMessage = async (message) => {
  try {
    debug.firebase('Firestore', 'Sending message', { 
      sender: message.senderId,
      organiser: message.organiserUID,
      talent: message.talentUID,
      bookingId: message.bookingId
    });
    
    validateDocId(message.senderId);
    validateDocId(message.organiserUID);
    validateDocId(message.talentUID);
    validateDocId(message.bookingId);
    
    if (!message.content || message.content.trim() === "") {
      throw new Error("Message content cannot be empty");
    }
    
    // Simplified message structure - removed unnecessary fields
    const messageData = {
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      sender: message.senderName || 'Unknown User',  // Use name instead of UID
      senderId: message.senderId,  // Keep for compatibility
      content: message.content,
      createdAt: new Date(),
      read: false,
      delivered: false
      // Removed: attachments, readAt, deliveredAt
    };
    
    // Reference to the conversation document using bookingId
    const conversationRef = doc(db, "Messages", message.bookingId);
    const conversationDoc = await getDoc(conversationRef);
    
    if (!conversationDoc.exists()) {
      debug.log('FirebaseIndex', 'Creating new conversation document', { bookingId: message.bookingId });
      // Create new conversation document with simplified structure
      await setDoc(conversationRef, {
        bookingId: message.bookingId,
        organiserUID: message.organiserUID,
        talentUID: message.talentUID,
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        messages: [messageData],
        // Simplified metadata - removed nested objects
        lastMessageTime: serverTimestamp(),
        unreadCount: message.senderId === message.talentUID ? 1 : 0  // Simple count for talent
      });
    } else {
      // Append message to existing conversation
      const existingData = conversationDoc.data();
      debug.log('FirebaseIndex', 'Updating existing conversation', { 
        bookingId: message.bookingId,
        existingMessageCount: existingData.messages?.length || 0
      });
      
      const recipientUID = message.senderId === message.organiserUID 
        ? message.talentUID 
        : message.organiserUID;
      
      await updateDoc(conversationRef, {
        messages: arrayUnion(messageData),
        lastUpdated: serverTimestamp(),
        lastMessageTime: serverTimestamp(),
        // Simplified unread count
        unreadCount: message.senderId === message.organiserUID ? increment(1) : 0
      });
    }
    
    debug.log('FirebaseIndex', 'Message sent successfully', { messageId: messageData.id });
    return { success: true, messageId: messageData.id };
  } catch (error) {
    debug.error('FirebaseIndex', 'Error sending message:', error);
    throw error;
  }
};

export const getConversation = async (bookingId, limit = 50, lastMessageId = null) => {
  try {
    debug.firebase('Firestore', 'Fetching conversation', { bookingId, limit });
    
    validateDocId(bookingId);
    
    let messagesQuery;
    
    if (lastMessageId) {
      const lastMessageDoc = await getDoc(doc(db, "Messages", lastMessageId));
      if (!lastMessageDoc.exists()) {
        throw new Error("Invalid last message ID");
      }
      
      messagesQuery = query(
        collection(db, "Messages"),
        where("bookingId", "==", bookingId),
        orderBy("createdAt", "desc"),
        startAfter(lastMessageDoc),
        limit(limit)
      );
    } else {
      messagesQuery = query(
        collection(db, "Messages"),
        where("bookingId", "==", bookingId),
        orderBy("createdAt", "desc"),
        limit(limit)
      );
    }
    
    const messagesSnapshot = await getDocs(messagesQuery);
    const messages = messagesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || null
    }));
    
    debug.log('FirebaseIndex', `Fetched ${messages.length} messages`, { bookingId });
    
    return {
      messages,
      lastMessageId: messages.length > 0 ? messages[messages.length - 1].id : null,
      hasMore: messages.length === limit
    };
  } catch (error) {
    debug.error('FirebaseIndex', 'Error fetching conversation:', error);
    throw error;
  }
};

export const markMessageAsRead = async (messageId) => {
  try {
    debug.firebase('Firestore', 'Marking message as read', { messageId });
    
    validateDocId(messageId);
    
    const messageRef = doc(db, "Messages", messageId);
    await updateDoc(messageRef, {
      read: true,
      readAt: serverTimestamp()
    });
    
    debug.log('FirebaseIndex', 'Message marked as read', { messageId });
    return true;
  } catch (error) {
    debug.error('FirebaseIndex', 'Error marking message as read:', error);
    throw error;
  }
};

// Booking Operations
export const createBooking = async (bookingData) => {
  try {
    debug.firebase('Firestore', 'Creating booking', { 
      organiser: bookingData.organiserUID,
      talent: bookingData.talentUID
    });
    
    validateDocId(bookingData.organiserUID);
    validateDocId(bookingData.talentUID);
    validateData(bookingData);
    
    const completeBookingData = {
      ...bookingData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: "pending_request"
    };
    
    const bookingRef = await addDoc(collection(db, "Bookings"), completeBookingData);
    
    debug.log('FirebaseIndex', 'Booking created successfully', { bookingId: bookingRef.id });
    return { success: true, bookingId: bookingRef.id };
  } catch (error) {
    debug.error('FirebaseIndex', 'Error creating booking:', error);
    throw error;
  }
};

export const updateBookingStatus = async (bookingId, status, actorUID) => {
  try {
    debug.firebase('Firestore', 'Updating booking status', { bookingId, status });
    
    validateDocId(bookingId);
    validateDocId(actorUID);
    
    const validStatuses = [
      "pending_request", 
      "talent_approved", 
      "agreement_generated", 
      "agreement_signed", 
      "payment_pending", 
      "payment_complete", 
      "transport_paid",
      "event_upcoming", 
      "talent_performance_complete", 
      "review_pending", 
      "closed",
      "cancelled"
    ];
    
    if (!validStatuses.includes(status)) {
      throw new Error(`Invalid status: ${status}`);
    }
    
    const bookingRef = doc(db, "Bookings", bookingId);
    await updateDoc(bookingRef, {
      status,
      updatedAt: serverTimestamp(),
      'StatusHistory.status': status,
      'StatusHistory.actor': actorUID,
      'StatusHistory.timestamp': serverTimestamp()
    });
    
    debug.log('FirebaseIndex', 'Booking status updated successfully', { bookingId, status });
    return true;
  } catch (error) {
    debug.error('FirebaseIndex', 'Error updating booking status:', error);
    throw error;
  }
};

export const getBooking = async (bookingId) => {
  try {
    debug.firebase('Firestore', 'Fetching booking', { bookingId });
    
    validateDocId(bookingId);
    const bookingRef = doc(db, "Bookings", bookingId);
    const bookingSnap = await getDoc(bookingRef);
    
    if (bookingSnap.exists()) {
      debug.log('FirebaseIndex', 'Booking fetched successfully', { bookingId });
      return { id: bookingSnap.id, ...bookingSnap.data() };
    } else {
      debug.log('FirebaseIndex', 'Booking not found', { bookingId });
      return null;
    }
  } catch (error) {
    debug.error('FirebaseIndex', 'Error fetching booking:', error);
    throw error;
  }
};

export const getUserBookings = async (userUID, userType, status = null) => {
  try {
    debug.firebase('Firestore', 'Fetching user bookings', { userUID, userType });
    
    validateDocId(userUID);
    
    const bookingsRef = collection(db, "Bookings");
    let bookingsQuery;
    
    // Determine the field to query based on user type
    const uidField = userType === "Talent" ? "talentUID" : "organiserUID";
    
    if (status) {
      bookingsQuery = query(
        bookingsRef,
        where(uidField, "==", userUID),
        where("status", "==", status),
        orderBy("createdAt", "desc")
      );
    } else {
      bookingsQuery = query(
        bookingsRef,
        where(uidField, "==", userUID),
        orderBy("createdAt", "desc")
      );
    }
    
    const bookingsSnapshot = await getDocs(bookingsQuery);
    const bookings = bookingsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    debug.log('FirebaseIndex', `Fetched ${bookings.length} bookings`, { userUID, userType });
    return bookings;
  } catch (error) {
    debug.error('FirebaseIndex', 'Error fetching user bookings:', error);
    throw error;
  }
};

// Booking Disputes Operations
export const createDispute = async (bookingId, disputeData) => {
  try {
    debug.firebase('Firestore', 'Creating booking dispute', { bookingId });
    
    validateDocId(bookingId);
    validateData(disputeData);
    
    const completeDisputeData = {
      ...disputeData,
      initiatedAt: serverTimestamp(),
      state: "DISPUTED"
    };
    
    const disputeRef = await addDoc(
      collection(db, "Bookings", bookingId, "disputes"), 
      completeDisputeData
    );
    
    // Update booking status to reflect dispute
    const bookingRef = doc(db, "Bookings", bookingId);
    await updateDoc(bookingRef, {
      status: "DISPUTE_PENDING",
      updatedAt: serverTimestamp()
    });
    
    debug.log('FirebaseIndex', 'Dispute created successfully', { 
      bookingId, 
      disputeId: disputeRef.id 
    });
    
    return { success: true, disputeId: disputeRef.id };
  } catch (error) {
    debug.error('FirebaseIndex', 'Error creating dispute:', error);
    throw error;
  }
};

export const getDisputes = async (bookingId) => {
  try {
    debug.firebase('Firestore', 'Fetching booking disputes', { bookingId });
    
    validateDocId(bookingId);
    
    const disputesRef = collection(db, "Bookings", bookingId, "disputes");
    const disputesSnapshot = await getDocs(disputesRef);
    
    const disputes = disputesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      initiatedAt: doc.data().initiatedAt?.toDate() || null,
      resolvedAt: doc.data().resolvedAt?.toDate() || null
    }));
    
    debug.log('FirebaseIndex', `Fetched ${disputes.length} disputes`, { bookingId });
    return disputes;
  } catch (error) {
    debug.error('FirebaseIndex', 'Error fetching disputes:', error);
    throw error;
  }
};

export const resolveDispute = async (bookingId, disputeId, resolutionData) => {
  try {
    debug.firebase('Firestore', 'Resolving booking dispute', { bookingId, disputeId });
    
    validateDocId(bookingId);
    validateDocId(disputeId);
    validateData(resolutionData);
    
    const disputeRef = doc(db, "Bookings", bookingId, "disputes", disputeId);
    await updateDoc(disputeRef, {
      ...resolutionData,
      state: "RESOLVED",
      resolvedAt: serverTimestamp()
    });
    
    // Update booking status to reflect dispute resolution
    const bookingRef = doc(db, "Bookings", bookingId);
    await updateDoc(bookingRef, {
      status: resolutionData.resolutionType === "CANCELLED" ? "cancelled" : "review_pending",
      updatedAt: serverTimestamp()
    });
    
    debug.log('FirebaseIndex', 'Dispute resolved successfully', { 
      bookingId, 
      disputeId 
    });
    
    return { success: true };
  } catch (error) {
    debug.error('FirebaseIndex', 'Error resolving dispute:', error);
    throw error;
  }
};

// Performance Agreement Operations
export const updatePerformanceAgreement = async (bookingId, agreementData) => {
  try {
    debug.firebase('Firestore', 'Updating performance agreement', { bookingId });
    
    validateDocId(bookingId);
    validateData(agreementData);
    
    const bookingRef = doc(db, "Bookings", bookingId);
    await updateDoc(bookingRef, {
      performanceAgreement: {
        ...agreementData,
        updatedAt: serverTimestamp()
      },
      updatedAt: serverTimestamp()
    });
    
    debug.log('FirebaseIndex', 'Performance agreement updated', { bookingId });
    return true;
  } catch (error) {
    debug.error('FirebaseIndex', 'Error updating performance agreement:', error);
    throw error;
  }
};

// Payment Operations
export const updatePaymentDetails = async (bookingId, paymentData) => {
  try {
    debug.firebase('Firestore', 'Updating payment details', { bookingId });
    
    validateDocId(bookingId);
    validateData(paymentData);
    
    const bookingRef = doc(db, "Bookings", bookingId);
    await updateDoc(bookingRef, {
      payment: {
        ...paymentData,
        updatedAt: serverTimestamp()
      },
      updatedAt: serverTimestamp()
    });
    
    debug.log('FirebaseIndex', 'Payment details updated', { bookingId });
    return true;
  } catch (error) {
    debug.error('FirebaseIndex', 'Error updating payment details:', error);
    throw error;
  }
};

// Ratings and Reviews Operations
export const submitRating = async (bookingId, ratingData) => {
  try {
    debug.firebase('Firestore', 'Submitting rating', { bookingId });
    
    validateDocId(bookingId);
    validateData(ratingData);
    
    const bookingRef = doc(db, "Bookings", bookingId);
    const bookingSnap = await getDoc(bookingRef);
    
    if (!bookingSnap.exists()) {
      throw new Error(`Booking not found: ${bookingId}`);
    }
    
    // Check if ratings array exists, if not create it
    const bookingData = bookingSnap.data();
    const ratings = bookingData.Ratings || [];
    
    // Add new rating to the array
    ratings.push({
      ...ratingData,
      createdAt: serverTimestamp()
    });
    
    // Update the booking with new ratings
    await updateDoc(bookingRef, {
      Ratings: ratings,
      updatedAt: serverTimestamp()
    });
    
    // If both parties have submitted ratings, update booking status
    const talentHasRated = ratings.some(r => r.talentRateOrganiser);
    const organiserHasRated = ratings.some(r => r.organiserRatetalent);
    
    if (talentHasRated && organiserHasRated) {
      await updateDoc(bookingRef, {
        status: "closed",
        updatedAt: serverTimestamp()
      });
    }
    
    debug.log('FirebaseIndex', 'Rating submitted successfully', { bookingId });
    return true;
  } catch (error) {
    debug.error('FirebaseIndex', 'Error submitting rating:', error);
    throw error;
  }
};

// Event Operations
export const updateEventDetails = async (bookingId, eventData) => {
  try {
    debug.firebase('Firestore', 'Updating event details', { bookingId });
    
    validateDocId(bookingId);
    validateData(eventData);
    
    const bookingRef = doc(db, "Bookings", bookingId);
    await updateDoc(bookingRef, {
      Event: {
        ...eventData,
        updatedAt: serverTimestamp()
      },
      updatedAt: serverTimestamp()
    });
    
    debug.log('FirebaseIndex', 'Event details updated', { bookingId });
    return true;
  } catch (error) {
    debug.error('FirebaseIndex', 'Error updating event details:', error);
    throw error;
  }
};

// Notification Operations
export const createNotification = async (userId, notificationData) => {
  try {
    debug.firebase('Firestore', 'Creating notification', { userId });
    
    validateDocId(userId);
    validateData(notificationData);
    
    const completeNotificationData = {
      ...notificationData,
      userId,
      isRead: false,
      createdAt: serverTimestamp()
    };
    
    const notificationRef = await addDoc(
      collection(db, "notifications"), 
      completeNotificationData
    );
    
    debug.log('FirebaseIndex', 'Notification created successfully', { 
      userId, 
      notificationId: notificationRef.id 
    });
    
    return { success: true, notificationId: notificationRef.id };
  } catch (error) {
    debug.error('FirebaseIndex', 'Error creating notification:', error);
    throw error;
  }
};

export const getUserNotifications = async (userId, limit = 20, unreadOnly = false) => {
  try {
    debug.firebase('Firestore', 'Fetching user notifications', { userId, limit, unreadOnly });
    
    validateDocId(userId);
    
    let notificationsQuery;
    
    if (unreadOnly) {
      notificationsQuery = query(
        collection(db, "notifications"),
        where("userId", "==", userId),
        where("isRead", "==", false),
        orderBy("createdAt", "desc"),
        limit(limit)
      );
    } else {
      notificationsQuery = query(
        collection(db, "notifications"),
        where("userId", "==", userId),
        orderBy("createdAt", "desc"),
        limit(limit)
      );
    }
    
    const notificationsSnapshot = await getDocs(notificationsQuery);
    
    const notifications = notificationsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || null
    }));
    
    debug.log('FirebaseIndex', `Fetched ${notifications.length} notifications`, { userId });
    return notifications;
  } catch (error) {
    debug.error('FirebaseIndex', 'Error fetching user notifications:', error);
    throw error;
  }
};

export const markNotificationAsRead = async (notificationId) => {
  try {
    debug.firebase('Firestore', 'Marking notification as read', { notificationId });
    
    validateDocId(notificationId);
    
    const notificationRef = doc(db, "notifications", notificationId);
    await updateDoc(notificationRef, {
      isRead: true,
      readAt: serverTimestamp()
    });
    
    debug.log('FirebaseIndex', 'Notification marked as read', { notificationId });
    return true;
  } catch (error) {
    debug.error('FirebaseIndex', 'Error marking notification as read:', error);
    throw error;
  }
};

export const markAllNotificationsAsRead = async (userId) => {
  try {
    debug.firebase('Firestore', 'Marking all notifications as read', { userId });
    
    validateDocId(userId);
    
    const notificationsRef = collection(db, "notifications");
    const unreadQuery = query(
      notificationsRef,
      where("userId", "==", userId),
      where("isRead", "==", false)
    );
    
    const unreadSnap = await getDocs(unreadQuery);
    
    const batch = [];
    unreadSnap.forEach((docSnap) => {
      const notificationRef = doc(db, "notifications", docSnap.id);
      batch.push(updateDoc(notificationRef, {
        isRead: true,
        readAt: serverTimestamp()
      }));
    });
    
    if (batch.length > 0) {
      await Promise.all(batch);
    }
    
    debug.log('FirebaseIndex', `Marked ${batch.length} notifications as read`, { userId });
    return { count: batch.length };
  } catch (error) {
    debug.error('FirebaseIndex', 'Error marking all notifications as read:', error);
    throw error;
  }
};

// System Notifications Operations
export const createSystemNotification = async (userId, title, body, data = {}) => {
  try {
    debug.firebase('Firestore', 'Creating system notification', { userId });
    
    validateDocId(userId);
    
    if (!title || !body) {
      throw new Error("Title and body are required for system notifications");
    }
    
    const notificationData = {
      userId,
      title,
      body,
      isRead: false,
      createdAt: serverTimestamp(),
      data
    };
    
    // Add routing information if provided
    if (data.route) {
      notificationData.route = data.route;
    }
    
    if (data.bookingId) {
      notificationData.bookingId = data.bookingId;
    }
    
    const notificationRef = await addDoc(
      collection(db, "systemNotifications"), 
      notificationData
    );
    
    debug.log('FirebaseIndex', 'System notification created successfully', { 
      userId, 
      notificationId: notificationRef.id 
    });
    
    return { success: true, notificationId: notificationRef.id };
  } catch (error) {
    debug.error('FirebaseIndex', 'Error creating system notification:', error);
    throw error;
  }
};

export const getSystemNotifications = async (userId, limit = 20) => {
  try {
    debug.firebase('Firestore', 'Fetching system notifications', { userId, limit });
    
    validateDocId(userId);
    
    const notificationsQuery = query(
      collection(db, "systemNotifications"),
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(limit)
    );
    
    const notificationsSnapshot = await getDocs(notificationsQuery);
    
    const notifications = notificationsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || null,
      readAt: doc.data().readAt?.toDate() || null
    }));
    
    debug.log('FirebaseIndex', `Fetched ${notifications.length} system notifications`, { userId });
    return notifications;
  } catch (error) {
    debug.error('FirebaseIndex', 'Error fetching system notifications:', error);
    throw error;
  }
};

export const markSystemNotificationAsRead = async (notificationId) => {
  try {
    debug.firebase('Firestore', 'Marking system notification as read', { notificationId });
    
    validateDocId(notificationId);
    
    const notificationRef = doc(db, "systemNotifications", notificationId);
    await updateDoc(notificationRef, {
      isRead: true,
      readAt: serverTimestamp()
    });
    
    debug.log('FirebaseIndex', 'System notification marked as read', { notificationId });
    return true;
  } catch (error) {
    debug.error('FirebaseIndex', 'Error marking system notification as read:', error);
    throw error;
  }
};

// Event Reminders Operations
export const createEventReminder = async (bookingId, reminderData) => {
  try {
    debug.firebase('Firestore', 'Creating event reminder', { bookingId });
    
    validateDocId(bookingId);
    validateData(reminderData);
    
    const completeReminderData = {
      ...reminderData,
      bookingId,
      status: "scheduled",
      createdAt: serverTimestamp()
    };
    
    const reminderRef = await addDoc(
      collection(db, "eventReminders"), 
      completeReminderData
    );
    
    debug.log('FirebaseIndex', 'Event reminder created successfully', { 
      bookingId, 
      reminderId: reminderRef.id 
    });
    
    return { success: true, reminderId: reminderRef.id };
  } catch (error) {
    debug.error('FirebaseIndex', 'Error creating event reminder:', error);
    throw error;
  }
};

export const updateReminderStatus = async (reminderId, status) => {
  try {
    debug.firebase('Firestore', 'Updating reminder status', { reminderId, status });
    
    validateDocId(reminderId);
    
    const validStatuses = ["scheduled", "sent", "failed", "acknowledged", "cancelled"];
    
    if (!validStatuses.includes(status)) {
      throw new Error(`Invalid status: ${status}`);
    }
    
    const reminderRef = doc(db, "eventReminders", reminderId);
    await updateDoc(reminderRef, {
      status,
      updatedAt: serverTimestamp(),
      ...(status === "sent" ? { sentAt: serverTimestamp() } : {}),
      ...(status === "acknowledged" ? { acknowledgedAt: serverTimestamp() } : {}),
      ...(status === "cancelled" ? { cancelledAt: serverTimestamp() } : {})
    });
    
    debug.log('FirebaseIndex', 'Reminder status updated successfully', { reminderId, status });
    return true;
  } catch (error) {
    debug.error('FirebaseIndex', 'Error updating reminder status:', error);
    throw error;
  }
};

export const getDueReminders = async () => {
  try {
    debug.firebase('Firestore', 'Fetching due reminders');
    
    const now = new Date();
    const reminderRef = collection(db, "eventReminders");
    
    // Query for scheduled reminders that are due
    const query = query(
      reminderRef,
      where("status", "==", "scheduled"),
      where("scheduledFor", "<=", now)
    );
    
    const querySnapshot = await getDocs(query);
    
    const reminders = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    debug.log('FirebaseIndex', `Fetched ${reminders.length} due reminders`);
    return reminders;
  } catch (error) {
    debug.error('FirebaseIndex', 'Error fetching due reminders:', error);
    throw error;
  }
};

// Analytics Operations
export const updateTalentAnalytics = async (talentUID, analyticsData) => {
  try {
    debug.firebase('Firestore', 'Updating talent analytics', { talentUID });
    
    validateDocId(talentUID);
    validateData(analyticsData);
    
    const analyticsRef = doc(db, "TalentAnalytics", talentUID);
    await updateDoc(analyticsRef, {
      ...analyticsData,
      updatedAt: serverTimestamp()
    });
    
    debug.log('FirebaseIndex', 'Talent analytics updated successfully', { talentUID });
    return true;
  } catch (error) {
    debug.error('FirebaseIndex', 'Error updating talent analytics:', error);
    throw error;
  }
};

export const updateOrganiserAnalytics = async (organiserUID, analyticsData) => {
  try {
    debug.firebase('Firestore', 'Updating organiser analytics', { organiserUID });
    
    validateDocId(organiserUID);
    validateData(analyticsData);
    
    const analyticsRef = doc(db, "OrganiserAnalytics", organiserUID);
    await updateDoc(analyticsRef, {
      ...analyticsData,
      updatedAt: serverTimestamp()
    });
    
    debug.log('FirebaseIndex', 'Organiser analytics updated successfully', { organiserUID });
    return true;
  } catch (error) {
    debug.error('FirebaseIndex', 'Error updating organiser analytics:', error);
    throw error;
  }
};

// Transaction History Logging
export const logTransactionHistory = async (bookingId, historyData) => {
  try {
    debug.firebase('Firestore', 'Logging transaction history', { bookingId });
    
    validateDocId(bookingId);
    validateData(historyData);
    
    const completeHistoryData = {
      ...historyData,
      timestamp: serverTimestamp(),
      bookingId
    };
    
    const historyRef = await addDoc(
      collection(db, "Bookings", bookingId, "transactionHistory"), 
      completeHistoryData
    );
    
    debug.log('FirebaseIndex', 'Transaction history logged successfully', { 
      bookingId, 
      historyId: historyRef.id 
    });
    
    return { success: true, historyId: historyRef.id };
  } catch (error) {
    debug.error('FirebaseIndex', 'Error logging transaction history:', error);
    throw error;
  }
};

export const getTransactionHistory = async (bookingId) => {
  try {
    debug.firebase('Firestore', 'Fetching transaction history', { bookingId });
    
    validateDocId(bookingId);
    
    const historyRef = collection(db, "Bookings", bookingId, "transactionHistory");
    const historyQuery = query(historyRef, orderBy("timestamp", "desc"));
    
    const historySnapshot = await getDocs(historyQuery);
    
    const history = historySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate() || null
    }));
    
    debug.log('FirebaseIndex', `Fetched ${history.length} transaction history items`, { bookingId });
    return history;
  } catch (error) {
    debug.error('FirebaseIndex', 'Error fetching transaction history:', error);
    throw error;
  }
};
